import type Phaser from 'phaser';
import type { EnemyRuntime } from '@/domain/combat/types';

export type TelegraphGeometry =
  | { kind: 'line'; x: number; y: number; length: number; width: number }
  | { kind: 'circle'; x: number; y: number; radius: number }
  | { kind: 'cone'; x: number; y: number; radius: number; halfAngle: number };

export function telegraphGeometry(enemy: EnemyRuntime): TelegraphGeometry {
  if (enemy.archetype === 'fast') {
    return { kind: 'line', x: enemy.x, y: enemy.y, length: Math.max(56, enemy.attackRange * 1.5), width: 18 };
  }
  if (enemy.archetype === 'tank') {
    return { kind: 'circle', x: enemy.x, y: enemy.y, radius: enemy.attackRange };
  }
  return { kind: 'cone', x: enemy.x, y: enemy.y, radius: enemy.attackRange, halfAngle: 10 };
}

export interface CombatTelegraphHandle {
  update(enemy: EnemyRuntime, playerX: number, playerY: number, nowMs: number): void;
  destroy(): void;
}

export function buildCombatTelegraph(
  scene: Phaser.Scene,
  enemy: EnemyRuntime,
  reducedMotion: boolean,
): CombatTelegraphHandle {
  const graphics = scene.add.graphics().setDepth(5);
  let lastSignature = '';

  function update(current: EnemyRuntime, playerX: number, playerY: number, nowMs: number): void {
    if (current.behavior.kind !== 'telegraph') {
      graphics.setVisible(false);
      lastSignature = '';
      return;
    }
    graphics.setVisible(true);
    const duration = Math.max(1, current.behavior.until - current.behavior.startedAt);
    const progress = Math.max(0, Math.min(1, (nowMs - current.behavior.startedAt) / duration));
    const progressStep = Math.floor(progress * 10);
    const angle = Math.atan2(playerY - current.y, playerX - current.x);
    const geometry = telegraphGeometry(current);
    const signature = `${current.behavior.attack}:${current.behavior.until}:${geometry.x}:${geometry.y}:${angle.toFixed(2)}:${progressStep}`;
    if (signature === lastSignature) return;
    lastSignature = signature;

    graphics.clear();
    graphics.setPosition(geometry.x, geometry.y).setRotation(geometry.kind === 'circle' ? 0 : angle);
    const fillAlpha = reducedMotion ? 0.12 : 0.08 + progress * 0.18;
    graphics.lineStyle(2, 0xffe08a, 0.95);
    graphics.fillStyle(0xe84f4f, fillAlpha);
    if (geometry.kind === 'line') {
      graphics.fillRect(0, -geometry.width / 2, geometry.length * progress, geometry.width);
      graphics.strokeRect(0, -geometry.width / 2, geometry.length, geometry.width);
      graphics.lineBetween(geometry.length * progress, -geometry.width / 2, geometry.length * progress, geometry.width / 2);
    } else if (geometry.kind === 'circle') {
      graphics.fillCircle(0, 0, geometry.radius * progress);
      graphics.strokeCircle(0, 0, geometry.radius);
      graphics.lineBetween(0, 0, geometry.radius, 0);
    } else {
      const radians = (geometry.halfAngle * Math.PI) / 180;
      graphics.beginPath();
      graphics.moveTo(0, 0);
      graphics.lineTo(Math.cos(-radians) * geometry.radius, Math.sin(-radians) * geometry.radius);
      graphics.arc(0, 0, geometry.radius, -radians, radians);
      graphics.closePath();
      graphics.fillPath();
      graphics.strokePath();
      graphics.lineBetween(0, 0, geometry.radius * progress, 0);
    }
  }

  update(enemy, enemy.x + 1, enemy.y, Date.now());
  return { update, destroy: () => graphics.destroy() };
}
